import { Button, Card, Modal, Pagination, Space, Table, Tag, Tooltip, notification } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { formatedDate } from 'utils/common';
import PageTitle from '../../components/page-title';
import styles from './styles.module.scss';
import CustomButton from 'components/custom-button';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { SERVER_FILE_ENDPOINT } from 'constants/envs';
import { shortenName } from 'utils/short-name';
import ModalCreateEdit from './component/ModalCreateEdit';
import Filter from './component/Filter';
import { TYPE_NEW, TYPE_SOUND } from '../../constants/common';

const LIMIT = 10;

const initFilter = { page: 1, limit: LIMIT };

// "demo/sounds/synthetic-4.wav" → "synthetic-4.wav"; tên quá dài thì giữ đầu + đuôi tệp.
const FILE_LABEL_MAX = 24;
const fileLabel = (link?: string) => {
  const name = (link || '').split('/').pop() || '';
  if (name.length <= FILE_LABEL_MAX) return name;
  return `${name.slice(0, FILE_LABEL_MAX - 11)}…${name.slice(-10)}`;
};

export interface UserFilter {
  limit: number;
  page: number;
  username?: string;
  email?: string;
  startDate?: string;
  endDate?: string;
}

const SoundsManager = () => {
  const [loading, setLoading] = useState(true);
  const [dataResponse, setDataResponse] = useState<any>({
    items: [],
    totalCount: 0,
  });
  const [filter, setFilter] = useState(initFilter as UserFilter);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [statusUpdateData, setStatusUpdateData] = useState(false);
  const [isOpenModalEditAndCreate, setIsOpenModalEditAndCreate] = useState(false);
  const [valueUserSelected, setvalueUserSelected] = useState<any>(null);

  const onFinish = (value: Partial<UserFilter>) => {
    setFilter((preState) => ({ ...preState, ...value, page: 1 }));
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  const hasSelected = selectedRowKeys.length > 0;

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.sounds, {
          ...filter,
        });
        setDataResponse({ items: res?.data?.data, totalCount: res?.data?.metadata?.total });
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [filter, statusUpdateData]);

  function renderTypeSound(type: TYPE_SOUND) {
    const actions = {
      MUSIC: 'Âm thanh',
      SOUND_EFFECT: 'Hiệu ứng',
      PAY_THE_BET: 'Trả kèo',
      SYNTHETIC: 'Tổng hợp',
    };

    return actions[type] || actions['MUSIC'];
  }

  const columns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 72,
      ellipsis: true,
      key: 'id',
    },
    {
      title: 'Tên âm thanh',
      dataIndex: 'name',
      key: 'name',
      render: (value) => (
        <Tooltip title={value}>
          <span>{shortenName(value || '', 60) || ''}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Tệp âm thanh',
      dataIndex: 'link',
      key: 'link',
      width: 220,
      // Chỉ hiện tên tệp (bỏ thư mục chung "demo/sounds/…" giống nhau ở mọi dòng), một dòng,
      // dài quá thì cắt ở giữa để vẫn thấy đuôi tệp; tooltip hiện đủ đường dẫn.
      render: (value) => (
        <Tooltip title={value}>
          <a
            href={`${SERVER_FILE_ENDPOINT}/${value}`}
            target="_blank"
            rel="noreferrer"
            className={styles.fileLink}
          >
            {fileLabel(value)}
          </a>
        </Tooltip>
      ),
    },
    {
      title: 'Loại',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (value) => <span>{renderTypeSound(value)}</span>,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 124,
      render: (value) =>
        value === TYPE_NEW.PUBLIC ? (
          <Tag color="green">Công khai</Tag>
        ) : value === TYPE_NEW.PRIVATE ? (
          <Tag>Ẩn</Tag>
        ) : (
          value
        ),
    },

    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 128,
      render: (value) => <span className={styles.nowrap}>{formatedDate(value)}</span>,
    },
    {
      title: 'Thao tác',
      width: 104,
      key: 'action',
      render: (_, record) => (
        <Tooltip title="Sửa">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handelOpenCreateEditModal(record)}
          />
        </Tooltip>
      ),
    },
  ];
  const onChangeStatusUpdateData = () => setStatusUpdateData(!statusUpdateData);

  const handleChangePage = (page: number) => {
    setFilter((preState: any) => ({ ...preState, page }));
  };

  const handelOpenDeleteModal = () => {
    setIsModalDeleteOpen(true);
  };

  const handleOkDeleteModal = async () => {
    setLoading(true);
    try {
      await AxiosUtils._delete(API.deleteSounds, { ids: selectedRowKeys });
      onChangeStatusUpdateData();
      notification.success({
        message: 'Thành công',
        description: 'Xóa thành công!',
        duration: 4,
      });
      setSelectedRowKeys([]);
    } catch (error) {
      notification.error({
        message: 'Không thành công',
        description: 'Có lỗi xảy ra, vui lòng thử lại.',
        duration: 4,
      });
    } finally {
      setLoading(false);
      setIsModalDeleteOpen(false);
    }
  };

  const handleCancelDeleteModal = () => {
    setIsModalDeleteOpen(false);
  };

  const handelOpenCreateEditModal = (value: any) => {
    setIsOpenModalEditAndCreate(true);
    setvalueUserSelected(value);
  };

  const handleCancelCreateEditModal = () => {
    setIsOpenModalEditAndCreate(false);
    setvalueUserSelected(null);
  };

  return (
    <div>
      <PageTitle
        title="Âm thanh"
        extra={
          <Space wrap>
            {hasSelected && (
              <Button danger icon={<DeleteOutlined />} onClick={handelOpenDeleteModal}>
                Xóa {selectedRowKeys.length} âm thanh
              </Button>
            )}
            <CustomButton
              onClick={() => handelOpenCreateEditModal(valueUserSelected)}
              bgColor="primary-green"
              text="Thêm âm thanh"
              icon={<PlusOutlined />}
            />
          </Space>
        }
      />
      <div className={styles.filterContainer}>
        <Filter onSubmit={onFinish} />
      </div>
      <Card
        className="custom-table"
        styles={{ body: { padding: 0 } }}
        style={{ overflow: 'hidden' }}
      >
        <Table
          rowSelection={rowSelection}
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={dataResponse.items}
          // Bảng rỗng thì bỏ scroll.x: ô "Trống" rộng hơn khung 2px → cuộn ngang thừa.
          scroll={dataResponse.items?.length ? { x: 940 } : undefined}
          loading={loading}
        />
        {dataResponse.totalCount > LIMIT && (
          <div className={styles.pagination}>
            <Pagination
              showSizeChanger={false}
              current={filter.page}
              onChange={handleChangePage}
              showLessItems
              total={dataResponse.totalCount}
              pageSize={LIMIT}
            />
          </div>
        )}
      </Card>

      {isOpenModalEditAndCreate && (
        <ModalCreateEdit
          isOpen={isOpenModalEditAndCreate}
          handleCancelCreateEditModal={handleCancelCreateEditModal}
          valueUserSelected={valueUserSelected}
          onChangeStatusUpdateData={onChangeStatusUpdateData}
        />
      )}
      <Modal
        title="Xóa âm thanh"
        open={isModalDeleteOpen}
        onOk={handleOkDeleteModal}
        onCancel={handleCancelDeleteModal}
        okText="Xóa"
        cancelText="Hủy"
        okButtonProps={{ danger: true }}
        confirmLoading={loading}
      >
        <p>Bạn có chắc muốn xóa {selectedRowKeys.length} âm thanh đã chọn?</p>
      </Modal>
    </div>
  );
};

export default SoundsManager;
